import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Maximize2 } from "lucide-react";
import { L, mediaUrl, useFetch, useTitle } from "../lib";
import { ArtImage, SampleTag, Lightbox, Reveal } from "../ui";
import PrintInquiryDialog from "./PrintInquiry";

const Meta = ({ label, value }) => value ? (
  <div className="py-3 border-b border-lou-line grid grid-cols-3 gap-4 text-sm">
    <dt className="text-lou-soft">{label}</dt><dd className="col-span-2">{value}</dd>
  </div>
) : null;

function ProcessStory({ a }) {
  const cap = (m) => a.image_captions?.[a.media_ids.indexOf(m)] || "";
  return (
    <section className="mt-24" data-testid="artwork-process">
      <p className="text-xs tracking-[0.28em] uppercase text-lou-clay">From stone to sculpture</p>
      <div className="grid md:grid-cols-2 gap-8 lg:gap-14 mt-8 items-end">
        {[["before", "In progress"], ["after", "Finished"]].map(([k, label]) => (
          <Reveal key={k} delay={k === "after" ? 0.12 : 0}>
            <figure data-testid={`artwork-process-${k}`}>
              <ArtImage src={mediaUrl(a.process[k])} alt={`${a.title || "Untitled"}, ${label.toLowerCase()}`} />
              <figcaption className="mt-3"><span className="font-lou text-xl">{label}</span>
                {cap(a.process[k]) && <span className="block text-sm text-lou-soft mt-1">{cap(a.process[k])}</span>}</figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function ArtworkDetail() {
  const { id } = useParams();
  const { data: a, error } = useFetch(`/artworks/${id}`, [id]);
  const [lb, setLb] = useState(null);
  const [showForm, setShowForm] = useState(false);
  useTitle(a ? a.title || "Untitled" : "Artwork");
  if (error) return <p className="max-w-6xl mx-auto px-6 py-24" data-testid="artwork-not-found">This artwork could not be found.</p>;
  if (!a) return <div className="h-[70vh]" />;
  const imgs = a.media_ids?.length ? a.media_ids.map((m) => mediaUrl(m)) : a.sample_urls || [];
  const items = imgs.map((src, i) => ({ src, title: a.title || "Untitled", caption: a.image_captions?.[i] || "" }));
  return (
    <article className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 sm:pt-16" data-testid="artwork-detail">
      <Link to={L(`/art?kind=${a.kind}`)} className="text-sm text-lou-soft hover:text-lou-clay">← {a.kind === "sculpture" ? "Sculptures" : "Paintings"}</Link>
      <div className="grid lg:grid-cols-12 gap-12 mt-8">
        <div className="lg:col-span-7 space-y-6">
          {items.length === 0 && <ArtImage src={null} alt="" />}
          {items.map((it, i) => (
            <button key={i} onClick={() => setLb(i)} className="group block w-full relative text-left" data-testid={`artwork-image-${i}`}
              aria-label={`View ${i === 0 ? "artwork" : `detail view ${i}`} full screen`}>
              <ArtImage src={it.src} alt={`${a.title || "Untitled"}${i ? `, view ${i + 1}` : ""}`} />
              <span className="absolute bottom-3 right-3 bg-lou-ink/70 text-white p-2 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity"><Maximize2 size={16} /></span>
              {it.caption && <span className="block text-xs text-lou-soft mt-2">{it.caption}</span>}
            </button>
          ))}
        </div>
        <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
          <Reveal>
            <SampleTag show={a.is_sample} />
            <h1 className="font-lou text-4xl sm:text-5xl font-light mt-3" data-testid="artwork-title">{a.title || "Untitled"}</h1>
            <p className="font-lou italic text-xl text-lou-soft mt-2">{a.year_text || "Undated"}</p>
            <dl className="mt-8 border-t border-lou-line">
              <Meta label="Medium" value={a.medium} /><Meta label="Dimensions" value={a.dimensions} />
              <Meta label="Credit" value={a.credit} /><Meta label="Rights" value={a.rights} />
            </dl>
            {a.description && <div className="mt-8 lou-prose text-lou-soft whitespace-pre-line" data-testid="artwork-description">{a.description}</div>}
            {a.print_available && (
              <div className="mt-10 border border-lou-line bg-lou-paper p-6 sm:p-8" data-testid="print-inquiry-panel">
                <p className="font-lou text-2xl">Request a Print</p>
                <p className="text-sm text-lou-soft leading-relaxed mt-2">Bring a piece of Louise’s creativity into your home. Contact Greg to explore print sizes, paper textures, framing, and delivery.</p>
                <button className="lou-btn mt-5" onClick={() => setShowForm(true)} data-testid="print-inquiry-open">Request a Print</button>
              </div>
            )}
          </Reveal>
        </div>
      </div>
      {a.process?.before && a.process?.after && <ProcessStory a={a} />}
      {a.related_stories?.length > 0 && (
        <section className="mt-24 max-w-3xl" data-testid="artwork-related-stories">
          <h2 className="font-lou text-3xl font-light mb-8">Memories of this work</h2>
          {a.related_stories.map((s) => (
            <blockquote key={s.id} className="border-l border-lou-clay/40 pl-6 mb-10">
              {s.title && <p className="font-lou text-xl">{s.title}</p>}
              <p className="text-lou-soft mt-2 leading-relaxed whitespace-pre-line">{s.body}</p>
              <footer className="text-sm mt-3">{s.display_name}</footer>
            </blockquote>
          ))}
        </section>
      )}
      <Lightbox items={items} index={lb} onClose={() => setLb(null)} onIndex={setLb} />
      {showForm && <PrintInquiryDialog art={a} image={items[0]?.src} onClose={() => setShowForm(false)} />}
    </article>
  );
}
